import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InfoIcon,
} from 'lucide-react';
import { useId, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { LocationMarkerIcon } from '@/components/icons/location-marker';
import { Banner } from '@/components/ui/banner';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Tag } from '@/components/ui/tag';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/utils/classnames';

export interface AdvancedControlsProps {
  /** Whether Talroo location screening is on for the campaign. */
  isLocationSensitive: boolean;
  onLocationSensitiveChange: (checked: boolean) => void;
  /** Openings in the campaign that are not remote, so Talroo can screen them. */
  screenableOpeningCount: number;
  /** Every opening in the campaign. */
  openingCount: number;
  /** Read-only campaign: the toggle shows its value but cannot change. */
  isReadOnly?: boolean;
  className?: string;
}

/**
 * Talroo campaign settings behind a collapsible header. Location screening
 * checks each applicant's commute, so remote openings are never screened: the
 * toggle is disabled when every opening is remote, and a campaign mixing both
 * screens only the on-site ones.
 */
export function AdvancedControls({
  isLocationSensitive,
  onLocationSensitiveChange,
  screenableOpeningCount,
  openingCount,
  isReadOnly = false,
  className,
}: AdvancedControlsProps) {
  const intl = useIntl();
  const toggleId = useId();
  const panelId = useId();
  const [isExpanded, setIsExpanded] = useState(false);

  const isAllRemote = screenableOpeningCount === 0;
  const isMixed = !isAllRemote && screenableOpeningCount < openingCount;
  const isOn = isLocationSensitive && !isAllRemote;

  return (
    <div
      className={cn(
        'bg-fx-neutral-50 border-fx-neutral-200 flex flex-col rounded-lg border',
        className,
      )}
    >
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={panelId}
        className="hover:bg-fx-neutral-100 flex items-center gap-3 rounded-lg p-4 text-left"
        onClick={() => setIsExpanded(expanded => !expanded)}
      >
        <Typography variant="body2" className="text-fx-neutral-600">
          <FormattedMessage
            defaultMessage="Advanced controls"
            description="Title of the collapsible section holding Talroo campaign settings such as location screening"
          />
        </Typography>
        {!isExpanded && isOn && (
          <span data-testid="location-screening-on-tag">
            <Tag
              className="bg-fx-brand-50"
              textClassName="text-fx-brand-600"
              icon={
                <LocationMarkerIcon className="text-fx-brand-600 size-4" />
              }
              text={intl.formatMessage({
                defaultMessage: 'Location screening on',
                description:
                  'Tag on the collapsed Advanced controls header while Talroo location screening is on',
              })}
            />
          </span>
        )}
        <span className="grow" />
        {isExpanded ? (
          <ChevronUpIcon className="text-fx-neutral-600 size-4" />
        ) : (
          <ChevronDownIcon className="text-fx-neutral-600 size-4" />
        )}
      </button>

      {isExpanded && (
        <div id={panelId} className="flex flex-col gap-3 rounded-b-lg bg-white">
          <Separator />
          <div className="flex flex-col gap-3 px-4 pb-4">
            <div className="flex items-start gap-2">
              <div className="flex shrink-0 pt-1">
                <Switch
                  id={toggleId}
                  checked={isOn}
                  disabled={isAllRemote || isReadOnly}
                  onCheckedChange={onLocationSensitiveChange}
                />
              </div>
              <div className="flex min-w-0 flex-col gap-0.5">
                <Typography variant="subtitle2" as="label" htmlFor={toggleId}>
                  <FormattedMessage
                    defaultMessage="Location screening"
                    description="Label of the toggle that turns on Talroo's commute check for applicants"
                  />
                </Typography>
                {isAllRemote ? (
                  <Typography variant="subtitle1" className="italic">
                    <FormattedMessage
                      defaultMessage="Not available for remote openings. Talroo needs a work location to check a commute."
                      description="Reason the Talroo location screening toggle is disabled when every opening in the campaign is remote"
                    />
                  </Typography>
                ) : (
                  <Typography variant="subtitle1">
                    {isMixed ? (
                      <FormattedMessage
                        defaultMessage="Checks each applicant's commute before they apply. Remote openings are skipped."
                        description="Description of Talroo location screening when the campaign has both remote and on-site openings"
                      />
                    ) : (
                      <FormattedMessage
                        defaultMessage="Checks each applicant's commute before they apply"
                        description="Description of the Talroo location screening toggle"
                      />
                    )}
                  </Typography>
                )}
                {isMixed && isOn && (
                  <Tag
                    className="bg-fx-neutral-100 mt-1.5"
                    icon={<CheckIcon className="text-fx-neutral-600 size-3" />}
                    text={intl.formatMessage(
                      {
                        defaultMessage:
                          '{screened} of {total} {total, plural, one {opening} other {openings}} screened',
                        description:
                          'Tag counting the on-site openings Talroo screens out of every opening in a campaign that mixes remote and on-site openings',
                      },
                      {
                        screened: screenableOpeningCount,
                        total: openingCount,
                      },
                    )}
                  />
                )}
              </div>
            </div>
            {isOn && (
              <div data-testid="location-screening-note">
                <Banner
                  icon={<InfoIcon className="text-fx-blue size-4" />}
                  color="blue"
                  description={intl.formatMessage({
                    defaultMessage:
                      'Expect fewer applicants, each at a higher cost. Talroo delivers best with a higher target cost per applicant.',
                    description:
                      'Note shown while Talroo location screening is on, stating the volume and cost trade-off',
                  })}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
